import { createContext, type ReactNode, useContext, useState } from 'react';

import type { ValueOf } from 'type-fest';

export const ACCORDION_OPEN_STATE = {
  EXPAND_ALL: 'EXPAND_ALL',
  COLLAPSE_ALL: 'COLLAPSE_ALL',
} as const;

export type AccordionOpenState = ValueOf<typeof ACCORDION_OPEN_STATE>;

type AccordionStateContextValue = {
  openState: AccordionOpenState;
  setOpenState: (state: AccordionOpenState) => void;
};

const AccordionStateContext = createContext<AccordionStateContextValue | null>(null);

export const useAccordionState = () => {
  const context = useContext(AccordionStateContext);
  if (!context) {
    throw new Error('useAccordionState must be used within AccordionStateProvider');
  }
  return context;
};

type AccordionStateProviderProps = {
  children: ReactNode;
};

export const AccordionStateProvider = ({ children }: AccordionStateProviderProps) => {
  // EXPAND_ALL = all sections are expanded, COLLAPSE_ALL = all sections are collapsed
  const [openState, setOpenState] = useState<AccordionOpenState>(ACCORDION_OPEN_STATE.COLLAPSE_ALL);

  return (
    <AccordionStateContext.Provider value={{ openState, setOpenState }}>{children}</AccordionStateContext.Provider>
  );
};
